import React, { useState } from 'react';
import { Popover, Button, Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { stripHtmlTags } from '../utils/renderHtml';

interface ExpandableCellProps {
  content: React.ReactNode;
  isHtml?: boolean;
  maxLength?: number;
}

/**
 * Cell renderer with "Show More" button and popover for long content
 * Used in DataGrid cells to handle expandable content
 */
const ExpandableCell: React.FC<ExpandableCellProps> = ({
  content,
  isHtml = false,
  maxLength = 100,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  // Convert content to string for length check
  const contentString =
    typeof content === 'string'
      ? isHtml
        ? stripHtmlTags(content)
        : content
      : String(content);

  const isTruncated = contentString.length > maxLength;
  const displayContent = isTruncated
    ? contentString.slice(0, maxLength) + '...'
    : contentString;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={styles.expandableCellContainer}>
      <Typography variant="body2" sx={styles.expandableCellText}>
        {isHtml ? (
          <span dangerouslySetInnerHTML={{ __html: displayContent }} />
        ) : (
          displayContent
        )}
      </Typography>

      {isTruncated && (
        <>
          <Button
            size="small"
            variant="text"
            onClick={handleClick}
            sx={styles.showMoreButton}>
            Show More
          </Button>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}>
            <Box sx={styles.popoverContent}>
              {isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: content as string }} />
              ) : (
                <Typography variant="body2">{content}</Typography>
              )}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

interface ExpandableListCellProps {
  items: string[];
  maxItems?: number;
  isHtml?: boolean;
}

/**
 * Cell renderer for list items with expand/collapse functionality
 * Shows first N items with "Show More" to see the rest
 */
export const ExpandableListCell: React.FC<ExpandableListCellProps> = ({
  items,
  maxItems = 3,
  isHtml = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (!items || items.length === 0) {
    return <Typography variant="body2">-</Typography>;
  }

  const isTruncated = items.length > maxItems;
  const displayItems = isTruncated ? items.slice(0, maxItems) : items;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={styles.expandableListContainer}>
      {displayItems.map((item, index) => (
        <Typography key={index} variant="body2" sx={styles.listItemText}>
          {isHtml ? (
            <span dangerouslySetInnerHTML={{ __html: stripHtmlTags(item) }} />
          ) : (
            item
          )}
        </Typography>
      ))}

      {isTruncated && (
        <>
          <Button
            size="small"
            variant="text"
            onClick={handleClick}
            sx={styles.listShowMoreButton}>
            +{items.length - maxItems} more
          </Button>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}>
            <Box sx={styles.listPopoverContent}>
              {items.map((item, index) => (
                <Typography
                  key={index}
                  variant="body2"
                  sx={styles.listPopoverItem}>
                  {isHtml ? (
                    <span
                      dangerouslySetInnerHTML={{ __html: stripHtmlTags(item) }}
                    />
                  ) : (
                    item
                  )}
                </Typography>
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
};

const styles: Record<string, SxProps<Theme>> = {
  expandableCellContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    width: '100%',
    overflow: 'hidden',
  },
  expandableCellText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  showMoreButton: {
    minWidth: 'auto',
    padding: '2px 8px',
    fontSize: '0.75rem',
    flexShrink: 0,
  },
  popoverContent: {
    p: 2,
    maxWidth: 600,
    maxHeight: 400,
    overflow: 'auto',
  },
  expandableListContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    width: '100%',
  },
  listItemText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listShowMoreButton: {
    alignSelf: 'flex-start',
    minWidth: 'auto',
    padding: '2px 8px',
    fontSize: '0.75rem',
  },
  listPopoverContent: {
    p: 2,
    maxWidth: 400,
    maxHeight: 400,
    overflow: 'auto',
  },
  listPopoverItem: {
    mb: 0.5,
  },
};

export default ExpandableCell;
